import { useState } from 'react';
import { LoadingState, PageHeader } from '@v2/design-system';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
    PiArrowLeftBold,
    PiCaretDownBold,
    PiCaretUpBold,
    PiCheckCircleBold,
    PiClockBold,
    PiFilePdfBold,
    PiReceiptBold,
} from 'react-icons/pi';
import FileDropzone from '@v2/components/ui/FileDropzone';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';
import type { CommissionComposition, CommissionMonthlyNf, PaymentApprovalNfRecord } from './types';

function fmt(v: number) {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
}

function fmtPct(v: number | null | undefined) {
    if (v == null || Number.isNaN(v)) return '—';
    return `${v.toFixed(2)}%`;
}

function fmtDate(value: string | null | undefined) {
    if (!value) return null;
    const raw = value.includes('T') ? value : value.replace(' ', 'T');
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return value.split(' ')[0] ?? value;
    return d.toLocaleDateString('pt-BR');
}

function CompositionCards({ composition }: { composition: CommissionComposition }) {
    const items = [
        (composition.current_period_count ?? 0) > 0
            ? { label: 'Mês atual', count: composition.current_period_count!, amount: composition.current_period_amount ?? 0 }
            : null,
        (composition.prior_sale_count ?? 0) > 0
            ? { label: 'Venda antiga', count: composition.prior_sale_count!, amount: composition.prior_sale_amount ?? 0 }
            : null,
        composition.deal_count > 0
            ? { label: 'Vendas (deal)', count: composition.deal_count, amount: composition.deal_amount }
            : null,
        composition.installment_count > 0
            ? { label: 'Parcelas (cascata)', count: composition.installment_count, amount: composition.installment_amount }
            : null,
        composition.salary_amount > 0
            ? { label: 'Salário', count: null as number | null, amount: composition.salary_amount }
            : null,
    ].filter(Boolean) as { label: string; count: number | null; amount: number }[];

    if (items.length === 0) return null;

    return (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {items.map((item) => (
                <div
                    key={item.label}
                    className="rounded-2xl border border-gray-200 bg-white px-4 py-3 dark:border-zinc-700 dark:bg-zinc-900"
                >
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{item.label}</p>
                    <p className="mt-1 text-lg font-bold text-gray-900 dark:text-white">{fmt(item.amount)}</p>
                    {item.count != null && (
                        <p className="text-xs text-gray-500">{item.count} registro(s)</p>
                    )}
                </div>
            ))}
        </div>
    );
}

function NfRecordRow({ record: r }: { record: PaymentApprovalNfRecord }) {
    return (
        <tr className="hover:bg-gray-50 dark:hover:bg-zinc-800/40">
            <td className="px-3 py-3">
                <span className={cn(
                    'inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold',
                    r.deal_age === 'prior_sale' && 'bg-amber-100 text-amber-900',
                    r.deal_age === 'current_period' && 'bg-emerald-100 text-emerald-800',
                    (!r.deal_age || r.deal_age === 'unknown') && 'bg-gray-100 text-gray-500',
                )}>
                    {r.deal_age_label ?? '—'}
                </span>
            </td>
            <td className="px-3 py-3">
                <span className={cn(
                    'inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold',
                    r.installment_kind === 'entry' && 'bg-rose-100 text-rose-800',
                    r.installment_kind === 'installment' && 'bg-violet-100 text-violet-800',
                    r.source_type === 'deal' && 'bg-sky-100 text-sky-800',
                )}>
                    {r.source_label}
                </span>
                {r.source_type === 'deal' && r.has_entry && (
                    <p className="mt-0.5 text-[10px] text-gray-400">Com entrada</p>
                )}
            </td>
            <td className="px-3 py-3">
                <p className="font-medium text-gray-900 dark:text-white">
                    {r.deal_number ?? `#${r.ploomes_deal_id}`}
                </p>
                <p className="text-xs text-gray-500">{r.client_name ?? r.deal_title ?? '—'}</p>
                {r.date && <p className="text-[11px] text-gray-400">Deal {r.date}</p>}
            </td>
            <td className="px-3 py-3 text-xs text-gray-600">{r.role_label}</td>
            <td className="whitespace-nowrap px-3 py-3 text-right text-gray-700 dark:text-zinc-300">
                {fmt(r.base_amount)}
            </td>
            <td className="whitespace-nowrap px-3 py-3 text-right text-gray-500">{fmtPct(r.rate_pp)}</td>
            <td className="whitespace-nowrap px-3 py-3 text-right font-semibold text-gray-900 dark:text-white">
                {fmt(r.final_amount)}
            </td>
        </tr>
    );
}

const STATUS_BADGE: Record<string, string> = {
    open: 'bg-gray-100 text-gray-700 dark:bg-zinc-800 dark:text-zinc-300',
    awaiting_nf: 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-200',
    nf_submitted: 'bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-200',
    paid: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200',
    cancelled: 'bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-200',
};

export default function CommissionMonthlyNfDetailPage() {
    const { nfId } = useParams<{ nfId: string }>();
    const navigate = useNavigate();
    const qc = useQueryClient();
    const [showSales, setShowSales] = useState(false);

    const { data, isLoading, isError } = useQuery<{ data: CommissionMonthlyNf }>({
        queryKey: ['commissioning', 'monthly-nf', nfId],
        queryFn: () => api.get(`/commissioning/monthly-nfs/${nfId}`).then((r) => r.data),
        enabled: !!nfId,
    });

    const nf = data?.data;

    const uploadMutation = useMutation({
        mutationFn: async (file: File) => {
            const form = new FormData();
            form.append('invoice', file);
            return api.post(`/commissioning/monthly-nfs/${nfId}/submit-nf`, form, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
        },
        onSuccess: () => {
            toast.success('NF enviada com sucesso!');
            qc.invalidateQueries({ queryKey: ['commissioning', 'monthly-nf', nfId] });
            qc.invalidateQueries({ queryKey: ['commissioning', 'monthly-nfs'] });
            qc.invalidateQueries({ queryKey: ['commissioning', 'my'] });
            navigate('/my-commission');
        },
        onError: (err: { response?: { data?: { message?: string } } }) =>
            toast.error(err?.response?.data?.message ?? 'Erro ao enviar NF. PDF ou XML, máx. 10 MB.'),
    });

    const canUpload = !!nf && ['awaiting_nf', 'open'].includes(nf.status);
    const alreadySubmitted = nf?.status === 'nf_submitted';
    const isPaid = nf?.status === 'paid';
    const deadlineLabel = fmtDate(nf?.nf_deadline_at);
    const hasSalary = (nf?.total_salary ?? 0) > 0;
    const deadlineUrgent = (nf?.days_until_deadline ?? 99) <= 3;

    return (
        <div className="w-full space-y-6 pb-12">
            <div className="flex items-start gap-3">
                <Link
                    to="/my-commission"
                    className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white shadow-sm hover:bg-gray-50 dark:border-zinc-700 dark:bg-zinc-900 dark:hover:bg-zinc-800"
                >
                    <PiArrowLeftBold className="size-4" />
                </Link>
                <PageHeader
                    className="min-w-0 flex-1 !mb-0"
                    title="Enviar Nota Fiscal"
                    subtitle={nf ? `${nf.period_label} · uma NF do mês` : 'Comissão mensal'}
                />
            </div>

            {isLoading && <LoadingState variant="detail" />}

            {isError && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
                    Não foi possível carregar esta NF mensal.
                </div>
            )}

            {nf && (
                <>
                    {/* Hero full-width */}
                    <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
                        <div className="grid gap-6 px-6 py-6 lg:grid-cols-[1fr_auto] lg:items-center lg:px-8">
                            <div className="flex flex-wrap items-end gap-x-10 gap-y-4">
                                <div>
                                    <p className="text-sm text-gray-500 dark:text-zinc-400">{nf.period_label}</p>
                                    <p className="mt-1 text-4xl font-black tracking-tight text-gray-900 dark:text-white">
                                        {fmt(nf.total_amount)}
                                    </p>
                                    <p className="mt-1 text-sm text-gray-500 dark:text-zinc-400">
                                        Total a emitir na NF
                                    </p>
                                </div>
                                <div className="pb-1">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Comissões</p>
                                    <p className="text-lg font-bold text-gray-900 dark:text-white">{fmt(nf.total_commissions)}</p>
                                    <p className="text-xs text-gray-400">{nf.records_count} venda(s)</p>
                                </div>
                                {hasSalary && (
                                    <div className="pb-1">
                                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Salário</p>
                                        <p className="text-lg font-bold text-gray-900 dark:text-white">{fmt(nf.total_salary)}</p>
                                    </div>
                                )}
                            </div>
                            <span className={cn('w-fit rounded-full px-3 py-1 text-xs font-bold', STATUS_BADGE[nf.status] ?? STATUS_BADGE.open)}>
                                {nf.status_label}
                            </span>
                        </div>

                        {deadlineLabel && canUpload && (
                            <div className={cn(
                                'flex items-center gap-2 border-t px-6 py-3 text-sm lg:px-8',
                                deadlineUrgent
                                    ? 'border-red-100 bg-red-50 text-red-800 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-200'
                                    : 'border-gray-100 bg-gray-50 text-gray-700 dark:border-zinc-800 dark:bg-zinc-800/50 dark:text-zinc-300',
                            )}>
                                <PiClockBold className="size-4 shrink-0" />
                                <span>
                                    Prazo <strong>{deadlineLabel}</strong>
                                    {nf.days_until_deadline != null && (
                                        <>
                                            {' · '}
                                            {nf.days_until_deadline === 0
                                                ? 'vence hoje'
                                                : `${nf.days_until_deadline} dia(s) restantes`}
                                        </>
                                    )}
                                </span>
                            </div>
                        )}
                    </section>

                    {/* Upload (largo) + checklist ao lado no desktop */}
                    {canUpload && (
                        <section className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-zinc-700 dark:bg-zinc-900 lg:p-8">
                            <div className="mb-6 flex items-start gap-3">
                                <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-gray-900 text-white dark:bg-white dark:text-gray-900">
                                    <PiReceiptBold className="size-5" />
                                </span>
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                        Anexe o arquivo da NF
                                    </h2>
                                    <p className="mt-0.5 text-sm text-gray-500 dark:text-zinc-400">
                                        Depois do envio, o RH confere e libera o pagamento.
                                    </p>
                                </div>
                            </div>

                            <div className="grid gap-6 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] lg:items-stretch">
                                <FileDropzone
                                    accept=".pdf,.xml,application/pdf,text/xml"
                                    acceptLabel="PDF ou XML"
                                    maxSizeMb={10}
                                    loading={uploadMutation.isPending}
                                    disabled={uploadMutation.isPending}
                                    label="Arraste a NF aqui"
                                    hint="ou clique para selecionar o arquivo"
                                    className="h-full"
                                    zoneClassName="h-full min-h-[220px] border-gray-300 bg-gray-50 dark:border-zinc-600 dark:bg-zinc-950/40"
                                    onFiles={(files) => {
                                        if (files[0]) uploadMutation.mutate(files[0]);
                                    }}
                                    onError={(msg) => toast.error(msg)}
                                />

                                <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-1">
                                    {[
                                        <>Valor total = <strong>{fmt(nf.total_amount)}</strong></>,
                                        <>Descrição: <strong>Comissões {nf.period_label}</strong></>,
                                        <>Arquivo em PDF ou XML (até 10 MB)</>,
                                        <>Uma NF do mês inteiro — não envie por venda</>,
                                    ].map((item, i) => (
                                        <li
                                            key={i}
                                            className="flex items-start gap-2.5 rounded-xl bg-gray-50 px-3.5 py-3 text-sm text-gray-700 dark:bg-zinc-800/60 dark:text-zinc-300"
                                        >
                                            <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-gray-200 text-[10px] font-bold text-gray-700 dark:bg-zinc-700 dark:text-zinc-200">
                                                {i + 1}
                                            </span>
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </section>
                    )}

                    {alreadySubmitted && (
                        <section className="rounded-2xl border border-blue-200 bg-blue-50 p-6 dark:border-blue-800 dark:bg-blue-950/30">
                            <div className="flex items-center gap-2">
                                <PiFilePdfBold className="size-5 text-blue-600 dark:text-blue-300" />
                                <p className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                                    NF enviada — aguardando aprovação do RH
                                </p>
                            </div>
                            {nf.invoice_submitted_at && (
                                <p className="mt-1 text-xs text-blue-700 dark:text-blue-300">
                                    Enviada em {fmtDate(nf.invoice_submitted_at)}
                                </p>
                            )}
                            {nf.invoice_url && (
                                <a
                                    href={nf.invoice_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 underline dark:text-blue-300"
                                >
                                    <PiFilePdfBold className="size-3.5" />
                                    Ver arquivo enviado
                                </a>
                            )}
                        </section>
                    )}

                    {isPaid && (
                        <section className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 dark:border-emerald-800 dark:bg-emerald-950/30">
                            <div className="flex items-center gap-2">
                                <PiCheckCircleBold className="size-5 text-emerald-600" />
                                <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-100">
                                    Pagamento confirmado
                                </p>
                            </div>
                            {nf.paid_at && (
                                <p className="mt-1 text-xs text-emerald-700 dark:text-emerald-300">
                                    Pago em {fmtDate(nf.paid_at)}
                                    {nf.paid_by_name ? ` · ${nf.paid_by_name}` : ''}
                                </p>
                            )}
                        </section>
                    )}

                    {nf.composition && (
                        <CompositionCards composition={nf.composition} />
                    )}

                    {nf.records && nf.records.length > 0 && (
                        <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
                            <button
                                type="button"
                                onClick={() => setShowSales((v) => !v)}
                                className="flex w-full items-center justify-between px-6 py-4 text-left hover:bg-gray-50 dark:hover:bg-zinc-800/50"
                            >
                                <span className="text-sm font-semibold text-gray-800 dark:text-zinc-100">
                                    Itens da NF ({nf.records.length})
                                </span>
                                {showSales ? (
                                    <PiCaretUpBold className="size-4 text-gray-400" />
                                ) : (
                                    <PiCaretDownBold className="size-4 text-gray-400" />
                                )}
                            </button>
                            {showSales && (
                                <div className="space-y-2 border-t border-gray-100 px-4 py-4 dark:border-zinc-800 sm:px-6">
                                    <p className="text-xs text-gray-500">
                                        Mês atual = deal ganho no período da NF · Venda antiga = deal de mês anterior.
                                    </p>
                                    <div className="overflow-x-auto">
                                        <table className="w-full min-w-[780px] text-sm">
                                            <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:bg-zinc-800">
                                                <tr>
                                                    <th className="px-3 py-2.5 text-left">Negociação</th>
                                                    <th className="px-3 py-2.5 text-left">Origem</th>
                                                    <th className="px-3 py-2.5 text-left">Deal / Cliente</th>
                                                    <th className="px-3 py-2.5 text-left">Papel</th>
                                                    <th className="px-3 py-2.5 text-right">Base</th>
                                                    <th className="px-3 py-2.5 text-right">%</th>
                                                    <th className="px-3 py-2.5 text-right">Comissão</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                                                {nf.records.map((r) => (
                                                    <NfRecordRow key={r.id} record={r} />
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </section>
                    )}
                </>
            )}
        </div>
    );
}
